<template>
  <v-dialog :value="value" max-width="560px" persistent scrollable @input="$emit('input', $event)">
    <v-card class="rounded-lg region-form-dialog">
      <v-card-title class="pb-1">
        <span class="headline">{{ regionId ? 'Editar região' : 'Nova região' }}</span>
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-4 pb-2 region-form-dialog__body">
        <div class="region-form-dialog__fields-wrap">
          <v-overlay
            :value="loading_edit_overlay"
            absolute
            opacity="0.92"
            color="white"
            z-index="4"
            class="region-form-dialog__overlay rounded-b-0"
          >
            <div class="region-form-dialog__loading text-center px-4">
              <v-progress-circular indeterminate size="52" width="4" color="primary" />
              <div class="mt-4 text-subtitle-2 primary--text font-weight-medium">Carregando região…</div>
              <div class="mt-1 text-caption grey--text text--darken-1">Aguarde um instante</div>
            </div>
          </v-overlay>

          <!-- Edição: pai e tipo somente leitura -->
          <template v-if="regionId">
            <v-text-field
              :value="parent_display_label"
              outlined
              dense
              readonly
              disabled
              label="Região pai"
              prepend-inner-icon="mdi-file-tree"
              hide-details="auto"
              class="mb-4 region-form-dialog__readonly"
            />
            <v-text-field
              :value="kindLabel(form.kind)"
              outlined
              dense
              readonly
              disabled
              label="Tipo"
              prepend-inner-icon="mdi-shape-outline"
              hide-details="auto"
              class="mb-4 region-form-dialog__readonly"
            />
          </template>

          <!-- Criação: pode escolher pai e tipo conforme regras já existentes -->
          <template v-else>
            <v-autocomplete
              v-model="form.parent_id"
              :items="parent_items"
              :loading="loading_parents"
              :disabled="parent_locked_create"
              :clearable="!parent_locked_create"
              item-text="path_label"
              item-value="id"
              label="Região pai"
              prepend-inner-icon="mdi-file-tree"
              outlined
              dense
              hide-details="auto"
              class="mb-4"
            />
            <v-select
              v-model="form.kind"
              :items="kind_items"
              item-text="text"
              item-value="value"
              label="Tipo"
              outlined
              dense
              prepend-inner-icon="mdi-shape-outline"
              :disabled="kind_locked_create"
              hide-details="auto"
              class="mb-4"
            />
          </template>

          <v-text-field
            v-model="form.name"
            outlined
            dense
            label="Nome"
            prepend-inner-icon="mdi-map-marker-outline"
            hide-details="auto"
            class="mb-4"
            :disabled="loading_edit_overlay"
          />
          <v-text-field
            v-model="form.code"
            outlined
            dense
            label="Código"
            prepend-inner-icon="mdi-identifier"
            hide-details="auto"
            :disabled="loading_edit_overlay"
          />
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions class="justify-end py-3">
        <f-btn cancel @click="close" />
        <f-btn save :disabled="loading_edit_overlay || saving" @click="submit" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Api from '@/api/index'
import { flattenRegionsTree } from './flattenRegionsTree.js'

let KIND_ITEMS = [
  { value: 'country', text: 'País' },
  { value: 'state', text: 'Estado' },
  { value: 'rgint', text: 'Região Geográfica Intermediária' },
  { value: 'rgi', text: 'Região Geográfica Imediata' },
  { value: 'city', text: 'Município' },
  { value: 'regional', text: 'Zona' },
  { value: 'neighborhood', text: 'Bairro' },
]

let KIND_LABEL_MAP = {}
KIND_ITEMS.forEach((kind_option) => {
  KIND_LABEL_MAP[kind_option.value] = kind_option.text
})

export default {
  name: 'RegionFormDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    regionId: {
      type: Number,
      default: null,
    },
    initialParentId: {
      type: Number,
      default: null,
    },
    initialKind: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      loading_parents: false,
      parent_rows: [],
      saving: false,
      form: {
        name: '',
        code: '',
        kind: 'city',
        parent_id: null,
      },
      kind_items: KIND_ITEMS,
    }
  },
  computed: {
    loading_edit_overlay() {
      return !!(this.regionId && this.loading)
    },
    kind_locked_create() {
      return !!this.initialKind
    },
    parent_locked_create() {
      return this.initialParentId != null
    },
    parent_items() {
      let rows = this.parent_rows || []
      if (this.regionId == null) return rows
      return rows.filter((r) => r.id !== this.regionId)
    },
    parent_display_label() {
      if (!this.regionId) return ''
      let parent_id = this.form.parent_id
      if (parent_id == null || parent_id === '') return 'Raiz (sem região pai)'
      let parent_row = this.parent_rows.find((r) => r.id === Number(parent_id))
      return parent_row ? parent_row.path_label : `Região pai #${parent_id}`
    },
  },
  methods: {
    kindLabel(kind) {
      return KIND_LABEL_MAP[kind] || kind
    },
    loadParentRows() {
      this.loading_parents = true
      return Api.Region.index()
        .then((r) => r.data)
        .then((data) => {
          this.parent_rows = flattenRegionsTree(data)
        })
        .catch(() => {
          this.parent_rows = []
        })
        .finally(() => {
          this.loading_parents = false
        })
    },
    openDialog() {
      if (this.regionId) {
        this.loading = true
        Promise.all([this.loadParentRows(), Api.Region.show(this.regionId).then((r) => r.data)])
          .then(([, data]) => {
            let parent_id = data.parent_id != null ? Number(data.parent_id) : null
            this.form = {
              name: data.name || '',
              code: data.code != null ? String(data.code) : '',
              kind: data.kind || 'city',
              parent_id: Number.isFinite(parent_id) ? parent_id : null,
            }
          })
          .catch(() => {
            this.addNotification({ type: 'error', msg: 'Não foi possível carregar a região.' })
            this.close()
          })
          .finally(() => {
            this.loading = false
          })
      } else {
        this.loading = false
        this.loadParentRows()
        this.form = {
          name: '',
          code: '',
          kind: this.initialKind || 'city',
          parent_id: this.initialParentId,
        }
      }
    },
    close() {
      this.$emit('input', false)
    },
    submit() {
      let name = (this.form.name || '').trim()
      if (!name) {
        this.addNotification({ type: 'warning', msg: 'Informe o nome da região.' })
        return
      }
      let action = this.regionId ? 'update' : 'create'
      let payload
      if (this.regionId) {
        payload = {
          id: this.regionId,
          name,
          code: (this.form.code || '').trim(),
        }
      } else {
        payload = {
          name,
          code: (this.form.code || '').trim(),
          kind: this.form.kind,
          parent_id: this.form.parent_id,
        }
      }
      this.swallConfirmation({
        title: this.regionId ? 'Atualizar esta região?' : 'Cadastrar nova região?',
        icon: 'question',
      }).then((confirmed) => {
        if (!confirmed) return
        this.saving = true
        Api.Region[action](payload)
          .then(() => {
            if (this.regionId) {
              this.successUpdated()
            } else {
              this.successSaved()
            }
            this.$emit('saved')
            this.close()
          })
          .catch(() => {
            if (this.regionId) {
              this.errorUpdated()
            } else {
              this.errorCreated()
            }
          })
          .finally(() => {
            this.saving = false
          })
      })
    },
  },
  watch: {
    value(v) {
      if (v) this.openDialog()
    },
    regionId() {
      if (this.value) this.openDialog()
    },
  },
}
</script>

<style scoped>
.region-form-dialog__body {
  position: relative;
  min-height: 220px;
}

.region-form-dialog__fields-wrap {
  position: relative;
  min-height: 200px;
}

.region-form-dialog__overlay {
  border-radius: 0 0 12px 12px;
}

.region-form-dialog__loading {
  max-width: 260px;
  margin: 0 auto;
}

.region-form-dialog__readonly >>> .v-input__slot {
  background: rgba(0, 0, 0, 0.03) !important;
}
</style>
